import { FiClock } from "react-icons/fi";
import { RiEyeLine } from "react-icons/ri";
import { IoBedOutline, IoWaterOutline } from "react-icons/io5";
import { Col, Row } from "reactstrap";
import './PropertyCard.css';
import { API_URL, STORAGE_URL } from "../../utils/constants";
import moment from "moment";
import 'moment/locale/ar';
import { generateKey, printPropertyDescription, tField } from "../../utils/helpers";
import { Link, useLocation, useNavigate } from "react-router-dom";
import IconButton from "../Buttons/Icon";
import { SlPencil } from "react-icons/sl";
import { BsTrash2 } from "react-icons/bs";
import { IoReload, IoReloadOutline } from "react-icons/io5";
import { useState } from "react";
import { toast } from "react-toastify";
import { CiPlay1 } from "react-icons/ci";
import DeletePropertyModal from "../Modals/DeleteProperty";
import { GoPin } from "react-icons/go";
import BoostPropertyModal from "../Modals/BoostProperty";
import { useTranslation } from "react-i18next";
import RepostPropertyModal from "../Modals/RepostProperty";

function PropertyCard({property, showViews, refreshFunction}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [pauseLoading, setPauseLoading] = useState(false);
  const [deleteModalShown, setDeleteModalShown] = useState(false);
  const [boostModalShown, setBoostModalShown] = useState(false);
  const [repostModalShown, setRepostModalShown] = useState(false);

  if(!property) return null;

  const pause = (status) => {
    setPauseLoading(true);
    fetch(API_URL+'/user/properties/'+status+'/'+property.id, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    })
    .then(response => response.json())
    .then(data => {
      if(data.success) {
        if(status === 'activate')
          toast.success('تم تفعيل الإعلان بنجاح');
        else if(status === 'pause')
          toast.success('تم ايقاف الإعلان بنجاح');
        if(refreshFunction) refreshFunction();
      }
    })
    .catch(error => {
      console.error('Error:', error);
    })
    .finally(() => {
      setPauseLoading(false);
    });
  }

  const getImage = () => {
    if (property?.images?.length > 0) return STORAGE_URL + property.images[0].image_path;
    if (property?.videos?.length > 0 && property.videos[0].thumbnail_path) return STORAGE_URL + property.videos[0].thumbnail_path;
    return require('../../assets/images/default-office.png');
  };

  const isOwner = Number(property?.user_id) === Number(JSON.parse(localStorage.getItem('user'))?.id)
    && location.pathname.includes('/my_properties');

  const bedrooms = property?.bedrooms ?? 3;
  const bathrooms = property?.bathrooms ?? 2;

  return(
    <div className={'property-card' + (property?.is_premium ? ' property-card--premium' : '') + (property?.status === 'paused' ? ' property-card--paused' : '')}>
      <Link to={'/property/'+property?.id} state={{ property }} className="stretched-link"></Link>

      {/* Image */}
      <div className="property-card__image-wrap">
        <img src={getImage()} alt="" className="property-card__image" />
        <div className="property-card__image-overlay" />

        {Number(property?.is_premium) === 1 && (
          <span className="property-card__badge property-card__badge--premium">
            {t('property.premium_badge')}
          </span>
        )}

        {property?.status === 'paused' && (
          <span className="property-card__badge property-card__badge--paused">
            متوقف
          </span>
        )}
      </div>

      {/* Content */}
      <div className="property-card__body">
        <h5 className="property-card__title">{ tField(property, 'title') }</h5>

        <div className="property-card__meta">
          {property?.price && (
            <span className="property-card__price">
              {property.price} <small>د.ل</small>
            </span>
          )}
          <span className="property-card__meta-item">
            <FiClock size={13} />
            {property?.created_at &&
              moment(property.created_at)
                .fromNow()
                .replace(/[٠-٩]/g, d => '٠١٢٣٤٥٦٧٨٩'.indexOf(d))
            }
          </span>
          {showViews && (
            <span className="property-card__meta-item">
              <RiEyeLine size={13} /> {property?.views || 0}
            </span>
          )}
        </div>

        {/* Beds / Baths chips */}
        <div className="property-card__chips">
          <span className="property-card__chip">
            <IoBedOutline size={13} /> {bedrooms}
          </span>
          <span className="property-card__chip">
            <IoWaterOutline size={13} /> {bathrooms}
          </span>
        </div>

        {property?.description_ar && (
          <p className="property-card__desc">
            { printPropertyDescription(property.description_ar) }
          </p>
        )}
      </div>

      {/* Owner controls */}
      {isOwner && (
        <div className="property-card__actions" style={{position: 'relative', zIndex: 999}}>
          {property?.status !== 'active' ? (
            <IconButton size="sm" outline color="success" onClick={() => pause('activate')} loading={pauseLoading} disabled={pauseLoading} block>
              <IoReloadOutline />
            </IconButton>
          ) : (
            <IconButton size="sm" outline color="info" onClick={() => pause('pause')} loading={pauseLoading} disabled={pauseLoading} block>
              <CiPlay1 />
            </IconButton>
          )}
          <IconButton size="sm" outline color="dark" onClick={() => setRepostModalShown(true)} block>
            <IoReload />
          </IconButton>
          <IconButton size="sm" outline color="warning" onClick={() => setBoostModalShown(true)} block>
            <GoPin />
          </IconButton>
          <IconButton size="sm" outline color="secondary" onClick={() => navigate('/my_properties/create', { state: { property } })} block>
            <SlPencil />
          </IconButton>
          <IconButton size="sm" outline color="danger" onClick={() => setDeleteModalShown(true)} block>
            <BsTrash2 />
          </IconButton>
        </div>
      )}

      <DeletePropertyModal
        isOpen={deleteModalShown}
        toggle={() => setDeleteModalShown(!deleteModalShown)}
        propertyId={property?.id}
        onSuccess={() => { navigate('/my_properties?p='+generateKey()); }}
      />
      <BoostPropertyModal
        isOpen={boostModalShown}
        toggle={() => setBoostModalShown(!boostModalShown)}
        propertyId={property?.id}
        onSuccess={() => { navigate('/my_properties?p='+generateKey()); }}
      />
      <RepostPropertyModal
        isOpen={repostModalShown}
        toggle={() => setRepostModalShown(!repostModalShown)}
        propertyId={property?.id}
        onSuccess={() => { navigate('/my_properties?p='+generateKey()); }}
      />
    </div>
  );
}

export default PropertyCard;
